import React, { useEffect, useState, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { ArrowLeft, Phone, Printer, Download, FileSpreadsheet } from 'lucide-react';

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/88797887-5fbe-4b8e-86d9-2a8b6e9a51f8/images/24255a550df67aeec175ac877a674e8bae385938fef42044bf92b3a8217b6427.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EstimatePacket() {
  const { id } = useParams();
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const printRef = useRef();

  useEffect(() => {
    const fetchEstimate = async () => {
      try {
        // If no ID, fetch sample estimate
        const endpoint = id 
          ? `${API_URL}/api/estimates/public/${id}`
          : `${API_URL}/api/sample-estimate`;
        
        const { data } = await axios.get(endpoint);
        setEstimate(data);
      } catch (err) {
        console.error('Failed to fetch estimate:', err);
        setError('Failed to load estimate');
      } finally {
        setLoading(false);
      }
    };

    fetchEstimate();
  }, [id]);

  const handlePrint = () => {
    window.print();
  };

  const handleExportCSV = () => {
    if (!estimate) return;

    let csvContent = "data:text/csv;charset=utf-8,";
    
    // Company header
    csvContent += `"${estimate.company.name}"\n`;
    csvContent += `"${estimate.company.address}"\n`;
    csvContent += `"Phone: ${estimate.company.phone}"\n\n`;
    
    // Project info
    csvContent += `"Project:","${estimate.project.name}"\n`;
    csvContent += `"Address:","${estimate.project.address}"\n`;
    csvContent += `"Date:","${estimate.project.date}"\n`;
    csvContent += `"Estimate #:","${estimate.project.estimate_number}"\n\n`;

    // Column headers
    csvContent += `"Description","Qty","Unit","Labor","Material","Line Total"\n`;

    // Sections
    estimate.sections.forEach(section => {
      csvContent += `\n"${section.section_name}"\n`;
      section.items.forEach(item => {
        const lineTotal = (item.labor * item.quantity) + (item.material * item.quantity);
        csvContent += `"${item.description}",${item.quantity},"${item.unit}",${item.labor.toFixed(2)},${item.material.toFixed(2)},${lineTotal.toFixed(2)}\n`;
      });
      csvContent += `"${section.section_name} Subtotal","","",${section.subtotal_labor.toFixed(2)},${section.subtotal_material.toFixed(2)},${(section.subtotal_labor + section.subtotal_material).toFixed(2)}\n`;
    });

    // Totals
    csvContent += `\n"TOTALS"\n`;
    csvContent += `"Total Labor","","",${estimate.totals.total_labor.toFixed(2)}\n`;
    csvContent += `"Total Material","","","",${estimate.totals.total_material.toFixed(2)}\n`;
    csvContent += `"GRAND TOTAL","","","","","$${estimate.totals.grand_total.toLocaleString()}"\n`;

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Essex_Street_Estimate_${estimate.project.estimate_number}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F172A] mx-auto"></div>
          <p className="font-jakarta text-[#475569] mt-4">Loading estimate...</p>
        </div>
      </div>
    );
  }

  if (error || !estimate) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <p className="font-jakarta text-red-600 mb-4">{error || 'Estimate not found'}</p>
          <Link to="/">
            <Button className="bg-[#0F172A] text-white hover:bg-[#D4AF37] hover:text-[#0F172A]">
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Top Bar - No Print */}
      <div className="no-print bg-white border-b border-[#E2E8F0] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 text-[#0F172A] hover:text-[#D4AF37] transition-colors" data-testid="estimate-back-link">
              <ArrowLeft className="h-5 w-5" />
              <span className="font-jakarta font-medium">Back</span>
            </Link>
            
            <div className="flex items-center gap-3">
              <Button 
                onClick={handleExportCSV}
                variant="outline"
                className="border-[#0F172A] text-[#0F172A] hover:bg-[#0F172A] hover:text-white rounded-sm font-jakarta"
                data-testid="export-csv-btn"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
              <Button 
                onClick={handlePrint}
                className="bg-[#0F172A] text-white hover:bg-[#D4AF37] hover:text-[#0F172A] rounded-sm font-jakarta"
                data-testid="print-pdf-btn"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print / PDF
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Estimate Document */}
      <div ref={printRef} className="max-w-5xl mx-auto bg-white my-6 shadow-sm print:shadow-none print:my-0">
        {/* Company Header */}
        <header className="bg-[#0F172A] text-white p-8 print:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="Essex Street Contracting" className="h-16 w-auto" />
              <div>
                <h1 className="font-outfit text-2xl font-bold">{estimate.company.name}</h1>
                <p className="font-jakarta text-gray-300 text-sm">{estimate.company.address}</p>
                <p className="font-jakarta text-[#D4AF37] font-medium">{estimate.company.phone}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-outfit text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-1">ESTIMATE</p>
              <p className="font-mono text-sm text-gray-300">{estimate.project.estimate_number}</p>
              <p className="font-mono text-sm text-gray-300">{estimate.project.date}</p>
            </div>
          </div>
        </header>

        {/* Project Info */}
        <section className="p-8 border-b border-[#E2E8F0] print:p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h2 className="font-outfit text-lg font-semibold text-[#0F172A] mb-3">Project Information</h2>
              <div className="space-y-2">
                <p className="font-jakarta text-[#475569]">
                  <span className="font-semibold text-[#0F172A]">Project:</span> {estimate.project.name}
                </p>
                <p className="font-jakarta text-[#475569]">
                  <span className="font-semibold text-[#0F172A]">Location:</span> {estimate.project.address}
                </p>
              </div>
            </div>
            <div>
              <h2 className="font-outfit text-lg font-semibold text-[#0F172A] mb-3">Description</h2>
              <p className="font-jakarta text-[#475569] text-sm leading-relaxed">
                {estimate.project.description}
              </p>
            </div>
          </div>
        </section>

        {/* Estimate Tables */}
        {estimate.sections.map((section, sectionIndex) => (
          <section key={sectionIndex} className="p-8 border-b border-[#E2E8F0] estimate-section print:p-6">
            <h2 className="font-outfit text-xl font-bold text-[#0F172A] mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-[#D4AF37] text-[#0F172A] rounded-sm flex items-center justify-center text-sm font-bold">
                {sectionIndex + 1}
              </span>
              {section.section_name}
            </h2>
            
            <div className="overflow-x-auto">
              <table className="w-full estimate-table text-sm border-collapse" data-testid={`estimate-table-${sectionIndex}`}>
                <thead>
                  <tr className="bg-[#F1F5F9]">
                    <th className="text-left p-3 border border-[#E2E8F0] font-outfit">Description</th>
                    <th className="text-center p-3 border border-[#E2E8F0] font-outfit w-16">Qty</th>
                    <th className="text-center p-3 border border-[#E2E8F0] font-outfit w-14">Unit</th>
                    <th className="text-right p-3 border border-[#E2E8F0] font-outfit w-24">Labor</th>
                    <th className="text-right p-3 border border-[#E2E8F0] font-outfit w-24">Material</th>
                    <th className="text-right p-3 border border-[#E2E8F0] font-outfit w-28">Line Total</th>
                  </tr>
                </thead>
                <tbody className="font-mono text-xs">
                  {section.items.map((item, itemIndex) => {
                    const lineTotal = (item.labor * item.quantity) + (item.material * item.quantity);
                    return (
                      <tr key={itemIndex} className="hover:bg-[#F8FAFC]">
                        <td className="p-2 border border-[#E2E8F0] font-jakarta text-[#0F172A]">{item.description}</td>
                        <td className="p-2 border border-[#E2E8F0] text-center">{item.quantity}</td>
                        <td className="p-2 border border-[#E2E8F0] text-center">{item.unit}</td>
                        <td className="p-2 border border-[#E2E8F0] text-right">${item.labor.toFixed(2)}</td>
                        <td className="p-2 border border-[#E2E8F0] text-right">${item.material.toFixed(2)}</td>
                        <td className="p-2 border border-[#E2E8F0] text-right font-semibold">${lineTotal.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-[#F1F5F9] font-semibold">
                  <tr>
                    <td className="p-2 border border-[#E2E8F0] font-outfit text-[#0F172A]" colSpan="3">
                      {section.section_name} Subtotal
                    </td>
                    <td className="p-2 border border-[#E2E8F0] text-right font-mono">${section.subtotal_labor.toLocaleString()}</td>
                    <td className="p-2 border border-[#E2E8F0] text-right font-mono">${section.subtotal_material.toLocaleString()}</td>
                    <td className="p-2 border border-[#E2E8F0] text-right font-mono text-[#0F172A]">${(section.subtotal_labor + section.subtotal_material).toLocaleString()}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>
        ))}

        {/* Grand Totals */}
        <section className="p-8 bg-[#F8FAFC] print:p-6">
          <h2 className="font-outfit text-xl font-bold text-[#0F172A] mb-6">Estimate Summary</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Cost Breakdown */}
            <div className="bg-white border border-[#E2E8F0] rounded-sm p-6">
              <h3 className="font-outfit font-semibold text-[#0F172A] mb-4">Cost Breakdown</h3>
              <div className="space-y-3 font-mono text-sm">
                <div className="flex justify-between">
                  <span className="text-[#475569]">Total Labor:</span>
                  <span className="font-semibold">${estimate.totals.total_labor.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#475569]">Total Material:</span>
                  <span className="font-semibold">${estimate.totals.total_material.toLocaleString()}</span>
                </div>
                <div className="flex justify-between pt-3 border-t border-[#E2E8F0]">
                  <span className="text-[#0F172A] font-semibold">Combined Total:</span>
                  <span className="font-bold text-[#0F172A]">${(estimate.totals.total_labor + estimate.totals.total_material).toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Section Summary */}
            <div className="bg-white border border-[#E2E8F0] rounded-sm p-6">
              <h3 className="font-outfit font-semibold text-[#0F172A] mb-4">Section Summary</h3>
              <div className="space-y-3 font-mono text-sm">
                {estimate.sections.map((section, idx) => (
                  <div key={idx} className="flex justify-between">
                    <span className="text-[#475569]">{section.section_name}:</span>
                    <span className="font-semibold">${(section.subtotal_labor + section.subtotal_material).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Grand Total */}
          <div className="mt-8 bg-[#0F172A] text-white p-6 rounded-sm" data-testid="grand-total-section">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-outfit text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-1">PROJECT TOTAL</p>
                <p className="font-jakarta text-sm text-gray-300">Labor + Materials</p>
              </div>
              <p className="font-mono text-4xl font-bold text-[#D4AF37]" data-testid="grand-total-amount">
                ${estimate.totals.grand_total.toLocaleString()}
              </p>
            </div>
          </div>
        </section>

        {/* Notes Section */}
        <section className="p-8 border-t border-[#E2E8F0] print:p-6">
          <h2 className="font-outfit text-lg font-semibold text-[#0F172A] mb-4">Terms & Conditions</h2>
          <ul className="space-y-2">
            {estimate.notes.map((note, index) => (
              <li key={index} className="font-jakarta text-sm text-[#475569] flex items-start gap-2">
                <span className="text-[#D4AF37] mt-1">•</span>
                {note}
              </li>
            ))}
          </ul>
        </section>

        {/* Footer */}
        <footer className="bg-[#F1F5F9] p-6 border-t border-[#E2E8F0] print:p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Essex Street Contracting" className="h-8 w-auto" />
              <div>
                <p className="font-outfit font-bold text-[#0F172A] text-sm">{estimate.company.name}</p>
                <p className="font-jakarta text-[#475569] text-xs">{estimate.company.address}</p>
              </div>
            </div>
            <p className="font-jakarta text-[#D4AF37] font-semibold">{estimate.company.phone}</p>
          </div>
        </footer>
      </div>

      {/* Bottom Actions - No Print */}
      <div className="no-print max-w-5xl mx-auto pb-8 px-4">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white border border-[#E2E8F0] rounded-sm p-4">
          <p className="font-jakarta text-[#475569] text-sm">
            This estimate is ready to submit to your insurance company.
          </p>
          <div className="flex items-center gap-3">
            <Button 
              onClick={handleExportCSV}
              variant="outline"
              className="border-[#0F172A] text-[#0F172A] hover:bg-[#0F172A] hover:text-white rounded-sm font-jakarta"
              data-testid="bottom-export-csv-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Google Sheets (CSV)
            </Button>
            <Button 
              onClick={handlePrint}
              className="bg-[#D4AF37] text-[#0F172A] hover:bg-[#0F172A] hover:text-white rounded-sm font-jakarta"
              data-testid="bottom-print-btn"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print / Save PDF
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
